import { useEditor, EditorContent } from '@tiptap/react';
import { useRef, useState } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Minus, Link as LinkIcon, 
  Image as ImageIcon, Upload, Undo, Redo, Heading1, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Music
} from 'lucide-react';
import { uploadImage } from '../../services/storage';
import { storageService } from '../../services/api';
import { PiezaMultimedia } from './PiezaMultimedia';
import styles from './TiptapEditor.module.css';

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`${styles.menuButton} ${isActive ? styles.active : ''}`}
    title={title}
  >
    {children}
  </button>
);

// Sube el archivo a Firebase Storage directamente (mismo flujo que
// usa ImageUpload para el hero). Devuelve la URL pública descargable.
// La organización por artículo es opcional: si tenemos articleId lo
// usamos como subcarpeta; si no, va al folder genérico 'articles'.
async function uploadFileForArticle(file, articleId) {
  const folder = articleId ? `articles/${articleId}/inline` : 'articles/inline';
  return await uploadImage(file, folder);
}

const MenuBar = ({ editor, articleId, onNotify }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [subiendoPieza, setSubiendoPieza] = useState(false);
  const piezaInputRef = useRef(null);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL del enlace:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  // Inserta un audio o video en el punto donde está el cursor. La duración se
  // lee del propio archivo: preguntársela al redactor es pedirle un dato que el
  // navegador ya tiene.
  const insertarPieza = async (ev) => {
    const archivo = ev.target.files?.[0];
    ev.target.value = '';
    if (!archivo) return;
    const esVideo = archivo.type.startsWith('video');
    const tipo = esVideo ? 'video' : 'audio';
    try {
      setSubiendoPieza(true);
      const segundos = await new Promise((resolve) => {
        const el = document.createElement(esVideo ? 'video' : 'audio');
        el.preload = 'metadata';
        el.onloadedmetadata = () => { URL.revokeObjectURL(el.src);
          resolve(Number.isFinite(el.duration) ? Math.round(el.duration) : 0); };
        el.onerror = () => resolve(0);
        el.src = URL.createObjectURL(archivo);
      });
      // Agrupamos por artículo: el editor de artículos no conoce la edición, y
      // una pieza pertenece al artículo, no al número del periódico.
      const r = await storageService.subirMultimedia(
        articleId || '00000000-0000-0000-0000-000000000000', archivo, tipo);
      const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1';
      const src = `${base}/storage/proxy?gsUri=${encodeURIComponent(r.gsUri)}`;
      const titulo = window.prompt('Título de la pieza', archivo.name.replace(/\.[^.]+$/, '')) || '';
      editor.chain().focus().insertarPiezaMultimedia({
        tipo, src, titulo,
        autoria: '',
        duracion: segundos > 0 ? `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}` : '',
        enlace: '',
      }).run();
    } catch (e) {
      alert('No se pudo subir la pieza: ' + e.message);
    } finally {
      setSubiendoPieza(false);
    }
  };

  const addImageByUrl = () => {
    const url = window.prompt('URL de la imagen:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir reelegir el mismo archivo después
    if (!file) return;
    try {
      setUploading(true);
      const url = await uploadFileForArticle(file, articleId);
      if (url) editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      onNotify?.('No se pudo subir la imagen: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.menuBar}>
      <div className={styles.menuGroup}>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Título 1"
        >
          <Heading1 size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Título 2"
        >
          <Heading2 size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Título 3"
        >
          <Heading3 size={18} />
        </MenuButton>
      </div>

      <div className={styles.divider} />

      <div className={styles.menuGroup}>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Negrita (Ctrl+B)"
        >
          <Bold size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Cursiva (Ctrl+I)"
        >
          <Italic size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Subrayado (Ctrl+U)"
        >
          <UnderlineIcon size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Tachado"
        >
          <Strikethrough size={18} />
        </MenuButton>
      </div>

      <div className={styles.divider} />

      {/* Alineación de párrafo */}
      <div className={styles.menuGroup}>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Alinear a la izquierda"
        >
          <AlignLeft size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Centrar"
        >
          <AlignCenter size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Alinear a la derecha"
        >
          <AlignRight size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
          title="Justificar"
        >
          <AlignJustify size={18} />
        </MenuButton>
      </div>

      <div className={styles.divider} />

      <div className={styles.menuGroup}>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Lista con viñetas"
        >
          <List size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Lista numerada"
        >
          <ListOrdered size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Cita"
        >
          <Quote size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Línea horizontal"
        >
          <Minus size={18} />
        </MenuButton>
      </div>

      <div className={styles.divider} />

      <div className={styles.menuGroup}>
        <MenuButton
          onClick={addLink}
          isActive={editor.isActive('link')}
          title="Insertar enlace"
        >
          <LinkIcon size={18} />
        </MenuButton>
        <MenuButton
          onClick={addImageByUrl}
          title="Insertar imagen por URL"
        >
          <ImageIcon size={18} />
        </MenuButton>
        <MenuButton
          onClick={triggerFilePicker}
          disabled={uploading}
          title="Subir imagen desde el disco"
        >
          <Upload size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => piezaInputRef.current?.click()}
          disabled={subiendoPieza}
          title="Insertar audio o video (MP3 / MP4 — ver MULTIMEDIA.md)"
        >
          <Music size={18} />
        </MenuButton>
        <input
          ref={piezaInputRef}
          type="file"
          accept="audio/mpeg,video/mp4"
          onChange={insertarPieza}
          style={{ display: 'none' }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChosen}
          style={{ display: 'none' }}
        />
      </div>

      <div className={styles.divider} />

      <div className={styles.menuGroup}>
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Deshacer (Ctrl+Z)"
        >
          <Undo size={18} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Rehacer (Ctrl+Y)"
        >
          <Redo size={18} />
        </MenuButton>
      </div>

      {uploading && (
        <span className={styles.uploadingHint}>Subiendo imagen…</span>
      )}
    </div>
  );
};

export default function TiptapEditor({
  content,
  onChange,
  placeholder = 'Escribe aquí...',
  articleId,
  onNotify,
}) {
  const editor = useEditor({
    extensions: [
      // StarterKit ya trae link y underline en versiones recientes, y abajo se
      // configuran aparte: sin desactivarlos aquí, Tiptap avisa de extensiones
      // duplicadas y la configuración propia puede no aplicarse.
      StarterKit.configure({ link: false, underline: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'editor-link' },
      }),
      Image.configure({
        HTMLAttributes: { class: 'editor-image' },
      }),
      // Audio de entrevista o video de reportaje intercalado en el cuerpo. Se
      // serializa a HTML5 estándar, así que la web lo reproduce sin cambios y
      // el PDF lo convierte en un destacado con el enlace.
      PiezaMultimedia,
      // Alineación de texto — párrafos y headings. Cada nodo lleva
      // atributo textAlign que Tiptap serializa a style="text-align: ...".
      // Justify es lo que usan diarios; left es el default cuando no hay atributo.
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: 'left',
      }),
      Placeholder.configure({ placeholder }),
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      // Interceptar pegar y arrastrar imágenes: se suben a Firebase
      // Storage y se insertan como URL persistente en lugar del base64
      // masivo que Tiptap pondría por defecto.
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type?.startsWith('image/')) {
            const file = item.getAsFile();
            if (!file) continue;
            event.preventDefault();
            uploadFileForArticle(file, articleId)
              .then(url => url && editor?.chain().focus().setImage({ src: url }).run())
              .catch(err => onNotify?.('No se pudo subir la imagen: ' + err.message));
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false; // solo interesa arrastrar desde fuera
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const file = files[0];
        if (!file.type.startsWith('image/')) return false;
        event.preventDefault();
        uploadFileForArticle(file, articleId)
          .then(url => url && editor?.chain().focus().setImage({ src: url }).run())
          .catch(err => onNotify?.('No se pudo subir la imagen: ' + err.message));
        return true;
      },
    },
  });

  return (
    <div className={styles.editor}>
      <MenuBar editor={editor} articleId={articleId} onNotify={onNotify} />
      <EditorContent editor={editor} className={styles.content} />
    </div>
  );
}
